// @ts-strict-ignore
/* eslint-disable camelcase */
/**
 * @fileoverview GET/POST handler for the "/app_settings/test_user_logs" endpoint
 */
import { featureOn } from "@lib/featureFlipper";
import fetch from "@lib/fetch";
import { serializeQueryObjects } from "@lib/serializeQueryObjects";

import { reactQueryTableFetcherAdapter } from "../reactQueryFetcherAdapters";
import { appSettingsTestUserLogs } from "./constants";
import type { AppSettingsTestUserLogsParams, AppSettingsTestUserLogsResult } from "./types";

export const dataTableAppSettingsTestUserLogs = reactQueryTableFetcherAdapter(
  appSettingsTestUserLogs,
  (
    signal: AbortSignal,
    { appGroupId, query, groupby, limit = 20, start = 0, sortdir, sortby }: AppSettingsTestUserLogsParams
  ): Promise<AppSettingsTestUserLogsResult> => {
    // deconstruct the query object
    const {
      type,
      start_date,
      end_date,
      enabled_filters,
      device_attribute,
      events,
      user_attribute,
      ...clonedQuery
    } = query;

    // grab the search query
    const logSearch = clonedQuery[""];

    const filterableFields = [
      { enabled: enabled_filters?.includes("device_attribute"), key: "device_attribute", value: device_attribute },
      { enabled: enabled_filters?.includes("events"), key: "events", value: events },
      { enabled: enabled_filters?.includes("user_attribute"), key: "user_attribute", value: user_attribute },
    ];

    filterableFields.forEach(({ enabled, key, value }) => {
      if (enabled && value) {
        clonedQuery[key] = value;
      }
    });

    // if it's defined, map it to 'log_search' for this table
    if (logSearch) {
      clonedQuery["log_search"] = logSearch;
    }

    // delete the original
    delete clonedQuery[""];

    // if 'type' is an array, then map its values to a 'log_type' object
    if (Array.isArray(type)) {
      let showSdkLogs: boolean = (type as string[]).includes("SdkTestUserLogEntry");
      let showRestLogs: boolean = (type as string[]).includes("RestTestUserLogEntry");

      // remove filter if both are false
      if (!showSdkLogs && !showRestLogs) {
        showSdkLogs = showRestLogs = true;
      }

      clonedQuery["log_type"] = {
        show_sdk_logs: showSdkLogs,
        show_rest_logs: showRestLogs,
      };
    }

    // create a serializable query object
    const qs = serializeQueryObjects(clonedQuery) as object;

    // if both 'start_date' and 'end_date' are numbers, then map them to the next available key in an object
    if (typeof start_date === "number" && typeof end_date === "number") {
      const nextIdx = Object.keys(qs).length;

      qs[nextIdx] = {
        key: "date",
        start_date,
        end_date,
      };
    }

    const method = featureOn("event_user_log_post_fetch") ? "POST" : "GET";

    return fetch(
      "/app_settings/test_user_logs",
      method,
      {
        app_group_id: appGroupId,
        groupby,
        limit,
        start,
        sortdir,
        sortby,
        query: qs,
      },
      { signal }
    );
  }
  // TODO: reenable this after verifying that the validator
  // is correct for all valid responses
  // isAppSettingsTestUserLogsResult
);
